import React, { useState } from 'react';
import { REPORT, SectionTitle, SourceFootnote } from '../print/PrintAtoms';
import { KeyNumbers, type KeyNumberItem } from './KeyNumbers';
import type {
  BriefingData,
  ParcelDetail,
  GridScore,
  GridScoreComponent,
  GridComponentTier,
  GridNodeIntel,
  GridNodeEvidenceFact,
  RedFlag,
  DevelopableLand,
  GridSiteProfile,
} from '../../lib/api';

// ── Grid lens page-1 + node-intelligence section (grid-v2) ───────────────────
// Rendered ONLY when the briefing was fetched with ?profile=grid and the API
// returned a gridScore. Everything here reads from the payload — the only
// geographic condition anywhere is "nodeIntel present / absent", so a second
// curated node (Craig, AZ/NM/OK) renders without code changes. Deliberately
// self-contained: no shared BTM component is modified (DimensionBar,
// ResolveBeforeLOI, GradeCard stay untouched — they serve the protected
// client share links).
//
// grid-v2 (2026-07-14): the pass/fail hazard `gates` object is gone — flood /
// seismic / air are now the extensible red-flag SCREENS registry (flag+demote),
// and slope is the tract ranking engine (developable acres), not a gate. The
// water/fiber/land placeholder components were dropped from the rubric.
//
// Score-display honesty rules (design doc 2026-07-09):
// - No letter grades, no percentiles — no cross-parcel distribution basis yet.
// - Points-vs-max everywhere; drill-downs decompose to the exact total.
// - Every factor wears a tier chip: SCORED / CURATED / NEUTRAL / PROVISIONAL.
// - Provisional factors still show their real data; only the score is neutral.

const TIER_STYLE: Record<GridComponentTier, { bg: string; color: string }> = {
  scored: { bg: 'rgba(46,56,64,0.07)', color: REPORT.secondary },
  // Curated is provenance, not a verdict — keep it the neutral highlight blue-grey.
  // (It read as pass-green before, which flagged a mid-range score as an endorsement.)
  curated: { bg: 'rgba(80,98,114,0.10)', color: REPORT.highlight },
  neutral: { bg: 'rgba(46,56,64,0.07)', color: REPORT.tertiary },
  provisional: { bg: REPORT.caution + '1A', color: REPORT.cautionText },
};

// Committee-facing gloss for the computed dataConfidence tier (score-cell note).
const CONFIDENCE_NOTE: Record<GridScore['dataConfidence'], string> = {
  firm: 'firm-confidence data',
  partial: 'partial-confidence data',
  provisional: 'provisional data',
};

const TierChip: React.FC<{ tier: GridComponentTier }> = ({ tier }) => (
  <span style={{
    display: 'inline-block', fontFamily: 'monospace', fontSize: '7.5px', fontWeight: 600,
    letterSpacing: '0.1em', textTransform: 'uppercase', padding: '1px 5px', borderRadius: '3px',
    verticalAlign: '1px', marginLeft: '5px',
    background: TIER_STYLE[tier].bg, color: TIER_STYLE[tier].color,
  }}>
    {tier}
  </span>
);

const Meter: React.FC<{ frac: number }> = ({ frac }) => (
  <div style={{ height: '4px', borderRadius: '2px', background: 'rgba(46,56,64,0.10)', overflow: 'hidden' }}>
    <div style={{ height: '100%', borderRadius: '2px', background: REPORT.highlight, width: `${Math.max(0, Math.min(100, frac * 100))}%` }} />
  </div>
);

// ── Grid dimension bar — points-based sibling of DimensionBar ────────────────

interface GridDimRow {
  label: string;
  value: string;
  tier?: GridComponentTier; // chip on the row label (provisional data callouts)
  dim?: boolean;            // render value in tertiary (missing / not assessed)
}

interface GridDimensionBarProps {
  label: string;
  points: number;
  max: number;
  chip?: GridComponentTier; // headline-level chip (e.g. CURATED on Node Economics)
  rows: GridDimRow[];
  note?: { text: string; type: 'info' | 'confirmed' };
  components: GridScoreComponent[]; // exact decomposition for the expansion
  methNote?: string;
}

const GridDimensionBar: React.FC<GridDimensionBarProps> = ({ label, points, max, chip, rows, note, components, methNote }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ background: REPORT.surface, borderRadius: '6px', padding: '14px 16px', marginBottom: '6px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
        <div style={{ fontSize: '26px', fontWeight: 400, fontFamily: 'Georgia, serif', lineHeight: 1, color: REPORT.highlight, fontVariantNumeric: 'tabular-nums' }}>
          {points.toFixed(1)}<span style={{ fontSize: '13px', color: REPORT.secondary }}>/{max}</span>
        </div>
        <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'monospace', color: REPORT.highlight }}>
          {label}
          {chip && <TierChip tier={chip} />}
        </div>
      </div>
      <div style={{ marginBottom: '10px' }}><Meter frac={points / max} /></div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
            <div style={{ fontSize: '9px', color: REPORT.secondary, fontFamily: 'monospace', letterSpacing: '0.04em', textTransform: 'uppercase', flexShrink: 0 }}>
              {r.label}
              {r.tier && <TierChip tier={r.tier} />}
            </div>
            <div style={{ fontSize: '11px', fontWeight: r.dim ? 400 : 600, color: r.dim ? REPORT.tertiary : REPORT.heading, textAlign: 'right' }}>
              {r.value}
            </div>
          </div>
        ))}
      </div>
      {note && (
        <div style={{
          fontSize: '9px', marginTop: '8px', padding: '4px 8px', borderRadius: '3px', display: 'inline-block',
          background: note.type === 'confirmed' ? 'rgba(92,107,69,0.1)' : 'rgba(154,123,48,0.1)',
          color: note.type === 'confirmed' ? REPORT.pass : REPORT.caution,
          whiteSpace: 'pre-line', lineHeight: 1.5,
        }}>
          {note.text}
        </div>
      )}
      <div
        className="briefing-no-print"
        onClick={() => setExpanded(!expanded)}
        style={{ fontSize: '9px', color: REPORT.secondary, cursor: 'pointer', marginTop: '7px' }}
      >
        How we scored this {expanded ? '↑' : '↓'}
      </div>
      {expanded && (
        <div className="briefing-no-print" style={{ marginTop: '8px', padding: '10px 12px', borderRadius: '6px', background: 'rgba(80,98,114,0.06)' }}>
          <div style={{ fontSize: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: REPORT.heading, fontFamily: 'monospace', marginBottom: '4px' }}>
            Components (exact)
          </div>
          {components.map(c => (
            <div key={c.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', padding: '1px 0', gap: '8px' }}>
              <span style={{ color: REPORT.secondary }}>{c.label}<TierChip tier={c.tier} /></span>
              <span style={{ fontWeight: 500, color: REPORT.heading, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{c.points.toFixed(1)} / {c.max}</span>
            </div>
          ))}
          {methNote && (
            <div style={{ fontSize: '9px', color: REPORT.tertiary, fontStyle: 'italic', marginTop: '6px', lineHeight: 1.5 }}>
              {methNote}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Grid kill sheet — key numbers + red-flag screens + 3 grouped bars + resolve ──

const fmtMi = (v: number | string | null | undefined) => (v == null ? '—' : `${Number(v).toFixed(Number(v) < 1 ? 2 : 1)} mi`);

// HIFLD carries placeholder identifiers ("UNKNOWN202355", "TAP205172") for
// unnamed substations — treat those as no-name rather than rendering them.
const cleanSubName = (name: string | null | undefined): string | null => {
  if (!name) return null;
  return /^(UNKNOWN|TAP|NOT AVAILABLE)[\s\d]*$/i.test(name.trim()) ? null : name;
};

export const GridKillSheet: React.FC<{
  data: BriefingData;
  parcel: ParcelDetail;
  gridScore: GridScore;
  nodeIntel: GridNodeIntel | null;
  developableLand: DevelopableLand | null;
  redFlags: RedFlag[];
}> = ({ data, parcel, gridScore, nodeIntel, developableLand, redFlags }) => {
  const comp = Object.fromEntries(gridScore.components.map(c => [c.key, c])) as Record<string, GridScoreComponent>;
  const a = gridScore.anchor;
  const backbone = a.backbone_345kv;

  // Anchor name is only knowable when the wrapper's nearest-substation IS the
  // anchor (kv ≥ 138); the supplemental ≥138 kV lookup carries no name.
  const anchorIsWrapperSub = parcel.nearest_substation_kv != null && Number(parcel.nearest_substation_kv) >= 138;
  const anchorName = anchorIsWrapperSub ? cleanSubName(parcel.nearest_substation_name) : null;
  const secondSubName = cleanSubName(parcel.second_substation_name);

  // Node headroom cell: county-scoped EIA-860 retirements when present;
  // falls back to the dual-feed substation, then line count.
  const gens = data.powerInfra?.generators || [];
  const retiringMW = gens.reduce((s, g) => s + (Number(g.retiring_mw) || 0), 0);
  const earliestRetirement = gens
    .map(g => g.earliest_retirement)
    .filter((y): y is number => y != null)
    .reduce((min: number | null, y) => (min == null || y < min ? y : min), null);

  // Exact points-sum for the headline cell — the rubric promises drill-downs
  // decompose to the exact total, but the payload `total` is Math.round()ed
  // (e.g. 49.8 → 50), which wouldn't tie out against the visible components.
  const exactTotal = gridScore.components.reduce((s, c) => s + c.points, 0);
  const totalDisplay = Number.isInteger(exactTotal) ? String(exactTotal) : exactTotal.toFixed(1);

  const keyNumbers: KeyNumberItem[] = [
    {
      value: a.kv != null ? `${a.kv} kV · ${fmtMi(a.miles)}` : '—',
      label: anchorName ? `Interconnection anchor — ${anchorName}` : 'Interconnection anchor (≥138 kV)',
      note: a.lines != null ? `${a.lines} lines at yard` : undefined,
    },
    {
      value: backbone ? `${backbone.kv} kV · ${fmtMi(backbone.miles)}` : '—',
      label: '345 kV backbone tap',
    },
    retiringMW > 0
      ? {
          value: `${Math.round(retiringMW).toLocaleString()} MW`,
          label: `Node headroom · retiring ${earliestRetirement ?? ''}${earliestRetirement != null ? '+' : ''}`,
          note: 'EIA-860',
        }
      : parcel.second_substation_kv != null
        ? {
            value: `${Number(parcel.second_substation_kv)} kV · ${fmtMi(parcel.second_substation_miles)}`,
            label: `Dual feed — ${secondSubName || 'second substation'}`,
          }
        : { value: a.lines != null ? String(a.lines) : '—', label: 'Lines at anchor yard' },
    {
      value: `${totalDisplay} / ${gridScore.max}`,
      label: `Grid score · ${gridScore.rubric_version} rubric`,
      note: CONFIDENCE_NOTE[gridScore.dataConfidence],
    },
  ];

  // Red-flag screen registry (grid-v2) — flag+demote, not silent-gate. `redFlags`
  // carries only the FIRED screens; an empty list means every screen is clean.
  const hasRed = redFlags.some(f => f.severity === 'red');
  const hasWatch = redFlags.some(f => f.severity === 'watch');
  const screenHeader = hasRed
    ? '✗ DISQUALIFIER SCREEN FIRED'
    : hasWatch
      ? '⚠ WATCH-LEVEL SCREEN FLAGGED'
      : '✓ NO DISQUALIFIER SCREENS FIRED';
  const screenHeaderColor = hasRed ? REPORT.fail : hasWatch ? REPORT.caution : REPORT.pass;

  // Firming data row: real gas proximity when the payload has pipelines.
  const nearestGas = (data.pipelines || [])[0];
  const firmingValue = nearestGas
    ? `Gas ${nearestGas.distance_miles} mi (${nearestGas.system_name || nearestGas.operator || 'pipeline'})`
    : 'No pipeline within 10 mi';

  const curatedDate = nodeIntel?.curated_at ? new Date(nodeIntel.curated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

  const resolveItems: { what: string; why: string }[] = [
    {
      what: 'No interconnection study',
      why: 'The power anchor reflects measured proximity to existing steel — not a queue position or a signed study. A utility study request is the first diligence step.',
    },
    ...(comp.firming?.tier === 'provisional' ? [{
      what: 'Firming strategy unassessed',
      why: 'Interruptibility terms and on-site-generation optionality are held at a neutral placeholder pending commercial diligence.',
    }] : []),
    ...(nodeIntel ? [{
      what: 'Confirm the utility large-load contract path',
      why: `Curated node economics summarize research as of ${curatedDate || 'curation'} — tariff and contract terms move; verify current status with the utility before LOI.`,
    }] : []),
    ...redFlags.map(f => ({
      what: `${f.label} screen ${f.severity === 'red' ? 'fired' : 'flagged'}`,
      why: f.detail,
    })),
  ];

  // Developable-land rows: when no slope data exists yet, four stacked "—" reads
  // as broken rather than pending — collapse to a single explicit pending row.
  const hasSlopeData =
    developableLand?.developable_acres != null ||
    developableLand?.buildability_pct != null ||
    developableLand?.premium_flat_pct != null ||
    developableLand?.avg_slope_pct != null ||
    parcel.avg_slope_pct != null;
  const developableRows: GridDimRow[] = [
    { label: 'Ownership', value: parcel.owner_name || '—', dim: !parcel.owner_name },
    { label: 'Total acreage', value: parcel.acreage != null ? `${Number(parcel.acreage).toLocaleString()} ac` : '—', dim: parcel.acreage == null },
    ...(hasSlopeData ? [
      { label: 'Developable ac', value: developableLand?.developable_acres != null ? `${developableLand.developable_acres} ac` : '—', dim: developableLand?.developable_acres == null },
      { label: 'Buildable <10%', value: developableLand?.buildability_pct != null ? `${developableLand.buildability_pct}%` : '—', dim: developableLand?.buildability_pct == null },
      { label: 'Premium flat <5%', value: developableLand?.premium_flat_pct != null ? `${developableLand.premium_flat_pct}%` : '—', dim: developableLand?.premium_flat_pct == null },
      {
        label: 'Avg slope',
        value: developableLand?.avg_slope_pct != null
          ? `${developableLand.avg_slope_pct.toFixed(1)}%`
          : parcel.avg_slope_pct != null ? `${parcel.avg_slope_pct}%` : '—',
        dim: developableLand?.avg_slope_pct == null && parcel.avg_slope_pct == null,
      },
    ] : [
      { label: 'Developable land', value: 'Slope survey pending', dim: true },
    ]),
  ];

  return (
    <>
      {/* Key numbers — the anchor fact leads; the score is one cell, not the headline */}
      <div className="no-break">
        <KeyNumbers items={keyNumbers} />
      </div>

      {/* Red-flag screen strip — extensible disqualifier registry (flag+demote) */}
      <div className="no-break" style={{ marginBottom: '12px' }}>
        <div style={{
          fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', marginBottom: '8px',
          color: screenHeaderColor, textTransform: 'uppercase', fontFamily: 'monospace',
        }}>
          {screenHeader}
        </div>
        {redFlags.length === 0 ? (
          <div className="no-break" style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '5px 0' }}>
            <div style={{
              width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: REPORT.pass + '18', color: REPORT.pass, fontSize: '12px', fontWeight: 700,
            }}>
              ✓
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: REPORT.heading }}>All disqualifier screens clear</div>
              <div style={{ fontSize: '10px', color: REPORT.secondary }}>Mine void, floodplain, seismic, and air screens all pass. Slope drives ranking via developable acres, not a screen.</div>
            </div>
            <div style={{
              fontSize: '9px', fontWeight: 600, padding: '2px 8px', borderRadius: '3px',
              background: REPORT.pass + '15', color: REPORT.pass, fontFamily: 'monospace', letterSpacing: '0.04em',
            }}>
              CLEAN
            </div>
          </div>
        ) : (
          redFlags.map((flag, i) => {
            const isRed = flag.severity === 'red';
            const tone = isRed ? REPORT.fail : REPORT.caution;
            return (
              <div key={i} className="no-break" style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '5px 0', borderBottom: i < redFlags.length - 1 ? `1px solid ${REPORT.rule}80` : 'none',
              }}>
                <div style={{
                  width: '22px', height: '22px', borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: tone + '18', color: tone, fontSize: '12px', fontWeight: 700,
                }}>
                  {isRed ? '✗' : '⚠'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: REPORT.heading }}>{flag.label}</div>
                  <div style={{ fontSize: '10px', color: REPORT.secondary }}>{flag.detail}</div>
                </div>
                <div style={{
                  fontSize: '9px', fontWeight: 600, padding: '2px 8px', borderRadius: '3px',
                  background: tone + '15', color: tone, fontFamily: 'monospace', letterSpacing: '0.04em',
                }}>
                  {isRed ? 'RED' : 'WATCH'}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Dimension bars — 6 components grouped into 3 stories (50 / 27 / 8) */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div className="no-break" style={{ flex: 1, minWidth: '220px' }}>
            <GridDimensionBar
              label="Power & Interconnection"
              points={(comp.power?.points || 0) + (comp.firming?.points || 0)}
              max={(comp.power?.max || 40) + (comp.firming?.max || 10)}
              rows={[
                { label: 'Anchor substation', value: a.kv != null ? `${anchorName ? `${anchorName} ` : ''}${a.kv} kV · ${fmtMi(a.miles)}` : '—', dim: a.kv == null },
                { label: 'Lines at anchor', value: a.lines != null ? String(a.lines) : '—', dim: a.lines == null },
                {
                  label: 'Dual feed',
                  value: parcel.second_substation_kv != null
                    ? `${secondSubName ? `${secondSubName} ` : ''}${Number(parcel.second_substation_kv)} kV · ${fmtMi(parcel.second_substation_miles)}`
                    : '—',
                  dim: parcel.second_substation_kv == null,
                },
                { label: '345 kV backbone', value: backbone ? `${backbone.kv} kV · ${fmtMi(backbone.miles)}` : 'None in range', dim: !backbone },
                { label: 'Firming', tier: 'provisional', value: firmingValue, dim: !nearestGas },
              ]}
              note={a.kv != null && a.kv < 345 && backbone ? {
                text: `Existing yard is ${a.kv} kV — ${backbone.kv} kV service requires a line tap (new yard). The anchor scores the steel that exists today.`,
                type: 'info',
              } : undefined}
              components={[comp.power, comp.firming].filter(Boolean)}
              methNote="Power anchor = voltage × distance × line-count bands, with dual-feed and 345 kV-tap multipliers, from HIFLD steel via the on-demand proximity layer. Firming is a neutral placeholder gated on grid access — a gas-rich parcel with no wire collects almost nothing here."
            />
          </div>
          <div className="no-break" style={{ flex: 1, minWidth: '220px' }}>
            <GridDimensionBar
              label="Node Economics"
              points={(comp.utility?.points || 0) + (comp.price?.points || 0) + (comp.incentives?.points || 0)}
              max={(comp.utility?.max || 12) + (comp.price?.max || 8) + (comp.incentives?.max || 7)}
              chip={comp.utility?.tier === 'curated' ? 'curated' : 'neutral'}
              rows={[
                { label: 'Utility appetite', value: comp.utility ? `${comp.utility.points.toFixed(1)}/${comp.utility.max}` : '—' },
                { label: 'Power price', value: comp.price ? `${comp.price.points.toFixed(1)}/${comp.price.max}` : '—' },
                { label: 'Incentives & community', value: comp.incentives ? `${comp.incentives.points.toFixed(1)}/${comp.incentives.max}` : '—' },
              ]}
              note={nodeIntel ? {
                text: `${nodeIntel.display_name || nodeIntel.node_name} — evidence-backed research${curatedDate ? ` (${curatedDate})` : ''}; every claim cited in Grid Node Intelligence below.`,
                type: 'confirmed',
              } : {
                text: 'No curated grid node covers this parcel — all three factors held at neutral 0.5.',
                type: 'info',
              }}
              components={[comp.utility, comp.price, comp.incentives].filter(Boolean)}
              methNote="Curated 0–1 multipliers from the node's grid_node_intel row. A parcel outside every curated node holds neutral 0.5s (13.5/27)."
            />
          </div>
          <div className="no-break" style={{ flex: 1, minWidth: '220px' }}>
            <GridDimensionBar
              label="Developable Land & Site"
              points={comp.site?.points || 0}
              max={comp.site?.max || 8}
              rows={developableRows}
              components={[comp.site].filter(Boolean)}
              methNote="Site control scores ownership + readiness (grid target band 100–1,500 ac, not BTM's 2,500). Developable acres = total acreage × % under 10% grade — the tract-ranking metric, shown here as context; it is not part of the site-control points."
            />
          </div>
        </div>
        <div style={{ fontSize: '8.5px', color: REPORT.tertiary, fontFamily: 'monospace', letterSpacing: '0.03em', marginTop: '7px', lineHeight: 1.55 }}>
          SCORED computed from mapped data · CURATED analyst research, cited below · PROVISIONAL neutral placeholder pending diligence
        </div>
      </div>

      {/* Resolve before site commitment */}
      {resolveItems.length > 0 && (
        <div className="no-break" style={{
          border: `1.5px solid ${REPORT.caution}`, borderRadius: '6px',
          padding: '14px 16px', background: 'rgba(154,123,48,0.04)',
        }}>
          <div style={{
            fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', color: REPORT.caution,
            textTransform: 'uppercase', fontFamily: 'monospace', marginBottom: '8px',
          }}>
            {'⚠'} Resolve before site commitment
          </div>
          {resolveItems.map((item, i) => (
            <div key={i} style={{
              padding: '5px 0',
              borderBottom: i < resolveItems.length - 1 ? '1px solid rgba(154,123,48,0.12)' : 'none',
            }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: REPORT.heading }}>{item.what}</div>
              <div style={{ fontSize: '9.5px', color: REPORT.secondary, marginTop: '1px' }}>{item.why}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// ── Grid Node Intelligence section — the curated-evidence centerpiece ────────

const sourceLabel = (source: string): string => {
  try {
    return new URL(source).hostname.replace(/^www\./, '');
  } catch {
    return source.length > 48 ? source.slice(0, 45) + '…' : source;
  }
};

const CitedFacts: React.FC<{ facts: GridNodeEvidenceFact[] }> = ({ facts }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div
        className="briefing-no-print"
        onClick={() => setOpen(!open)}
        style={{ fontSize: '9px', color: REPORT.secondary, cursor: 'pointer', marginTop: '7px' }}
      >
        {facts.length} cited fact{facts.length === 1 ? '' : 's'} {open ? '↑' : '↓'}
      </div>
      {open && (
        <div style={{ marginTop: '8px', borderTop: `1px solid ${REPORT.rule}`, paddingTop: '4px' }}>
          {facts.map((f, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between', gap: '14px', alignItems: 'baseline',
              padding: '7px 0', borderBottom: i < facts.length - 1 ? `1px solid ${REPORT.rule}80` : 'none',
            }}>
              <div style={{ fontSize: '10.5px', color: REPORT.body, lineHeight: 1.5 }}>{f.fact}</div>
              <div style={{ fontFamily: 'monospace', fontSize: '8.5px', color: REPORT.tertiary, whiteSpace: 'nowrap', flexShrink: 0 }}>
                {sourceLabel(f.source)} {f.date ? `· ${f.date}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export const GridNodeSection: React.FC<{
  nodeIntel: GridNodeIntel | null;
  gridScore: GridScore;
}> = ({ nodeIntel, gridScore }) => {
  const comp = Object.fromEntries(gridScore.components.map(c => [c.key, c])) as Record<string, GridScoreComponent>;

  if (!nodeIntel) {
    return (
      <div className="briefing-section" style={{ background: REPORT.surface, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
        <div className="briefing-section-head">
          <SectionTitle>Grid Node Intelligence</SectionTitle>
        </div>
        <div style={{ fontSize: '11px', color: REPORT.secondary, lineHeight: 1.55 }}>
          No curated grid node covers this parcel &mdash; node economics (utility appetite, power price, incentives &amp; community) are held at neutral 0.5 multipliers ({gridScore.curatedTotal}/27). Curated node intelligence exists only where a node has been researched and seeded in <span style={{ fontFamily: 'monospace', fontSize: '10px' }}>grid_node_intel</span>.
        </div>
      </div>
    );
  }

  const ev = nodeIntel.evidence;
  const curatedDate = nodeIntel.curated_at ? new Date(nodeIntel.curated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;

  const factors: Array<{
    label: string;
    component: GridScoreComponent | undefined;
    score: number | null;
    rationale: string | null;
    facts: GridNodeEvidenceFact[];
  }> = [
    {
      label: 'Utility appetite & tariff',
      component: comp.utility,
      score: nodeIntel.utility_appetite_score != null ? Number(nodeIntel.utility_appetite_score) : null,
      rationale: ev?.utility_appetite_tariff?.rationale || null,
      facts: ev?.utility_appetite_tariff?.facts || [],
    },
    {
      label: 'Power price',
      component: comp.price,
      score: nodeIntel.power_price_score != null ? Number(nodeIntel.power_price_score) : null,
      rationale: ev?.power_price?.rationale || null,
      facts: ev?.power_price?.facts || [],
    },
    {
      label: 'Incentives & community',
      component: comp.incentives,
      score: nodeIntel.incentives_community_score != null ? Number(nodeIntel.incentives_community_score) : null,
      rationale: ev?.incentives_community?.rationale || null,
      facts: ev?.incentives_community?.facts || [],
    },
  ];

  const nodeFacts = ev?.node_facts || [];

  return (
    <div className="briefing-section" style={{ background: REPORT.surface, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
      <div className="briefing-section-head">
        <SectionTitle>Grid Node Intelligence</SectionTitle>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
        <div style={{ fontFamily: 'Georgia, serif', fontSize: '15px', color: REPORT.heading }}>
          {nodeIntel.display_name || nodeIntel.node_name}
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: '9px', color: REPORT.tertiary, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {curatedDate ? `Curated ${curatedDate} · ` : ''}{nodeIntel.radius_miles != null ? `${Number(nodeIntel.radius_miles)}-mi radius · ` : ''}parcel within radius
        </div>
      </div>
      <div style={{ fontSize: '11px', color: REPORT.body, lineHeight: 1.55, marginBottom: '12px', maxWidth: '78ch' }}>
        The three node-economics factors below are curated research, not map math &mdash; each score is a 0&ndash;1 multiplier on the rubric&rsquo;s 27 curated points, and every claim carries a source and date.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {factors.map((f, i) => (
          <div key={i} className="no-break" style={{ background: REPORT.white, borderRadius: '6px', padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'monospace', color: REPORT.heading }}>
                {f.label}
              </div>
              {f.component && (
                <div style={{ fontFamily: 'Georgia, serif', fontSize: '15px', color: REPORT.highlight, fontVariantNumeric: 'tabular-nums' }}>
                  {f.component.points.toFixed(1)}<span style={{ fontSize: '11px', color: REPORT.secondary }}>/{f.component.max}</span>
                </div>
              )}
            </div>
            {f.score != null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '7px 0 8px' }}>
                <div style={{ flex: 1 }}><Meter frac={f.score} /></div>
                <div style={{ fontFamily: 'monospace', fontSize: '9px', color: REPORT.secondary, whiteSpace: 'nowrap' }}>{f.score.toFixed(2)} / 1.0</div>
              </div>
            )}
            {f.rationale ? (
              <div style={{ fontSize: '11px', color: REPORT.body, lineHeight: 1.55, maxWidth: '78ch' }}>{f.rationale}</div>
            ) : (
              <div style={{ fontSize: '10px', color: REPORT.tertiary, fontStyle: 'italic' }}>
                Supporting citations held in the research file &mdash; the curated score above still drives the lens.
              </div>
            )}
            {f.facts.length > 0 && <CitedFacts facts={f.facts} />}
          </div>
        ))}
      </div>

      {nodeFacts.length > 0 && (
        <div style={{ marginTop: '10px', padding: '10px 14px', background: 'rgba(80,98,114,0.06)', borderRadius: '6px' }}>
          <div style={{ fontSize: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: REPORT.secondary, fontFamily: 'monospace', marginBottom: '4px' }}>
            Node facts
          </div>
          {nodeFacts.map((f, i) => (
            <div key={i} style={{ fontSize: '10.5px', color: REPORT.body, lineHeight: 1.5, padding: '2px 0' }}>
              {f.fact} <span style={{ fontFamily: 'monospace', fontSize: '8.5px', color: REPORT.tertiary }}>{sourceLabel(f.source)}{f.date ? ` · ${f.date}` : ''}</span>
            </div>
          ))}
        </div>
      )}

      <SourceFootnote>
        Curated node research (grid_node_intel) &middot; scores are 0&ndash;1 multipliers on the rubric&rsquo;s 27 curated points &middot; verify tariff/docket status before commitment.
      </SourceFootnote>
    </div>
  );
};

// ── Site & Incentives panel — curated KEIP site profile (KY EconDev / One East KY) ──
// Renders under ?profile=grid only when the node carries a `site_profile` block. Surfaces
// the marquee incentive stack (Energy Community / OZ lead), confirmed on-site utility
// service, and site facts — all cited. Self-contained; no shared BTM component touched.

const MARQUEE_INCENTIVES = new Set(['Energy Community', 'Opportunity Zone']);

export const GridSitePanel: React.FC<{ nodeIntel: GridNodeIntel | null }> = ({ nodeIntel }) => {
  const sp: GridSiteProfile | undefined = nodeIntel?.evidence?.site_profile;
  if (!sp) return null;

  const facts: Array<{ label: string; value: string }> = [];
  if (sp.owner) facts.push({ label: 'Owner', value: sp.owner });
  if (sp.zoning) facts.push({ label: 'Zoning', value: sp.zoning });
  if (sp.total_acres != null) facts.push({ label: 'Park size', value: `${sp.total_acres} ac` });
  if (sp.price) facts.push({ label: 'Asking', value: sp.price });
  if (sp.status && sp.status.length > 0) facts.push({ label: 'Status', value: sp.status.join(' · ') });

  const util = sp.utility || {};
  const utilRows: Array<{ label: string; value: string; hi?: boolean }> = [];
  if (util.electric) utilRows.push({ label: 'Electric', value: util.electric, hi: true });
  if (util.gas) utilRows.push({ label: 'Gas', value: util.gas });
  if (util.water) utilRows.push({ label: 'Water', value: util.water });
  if (util.sewer) utilRows.push({ label: 'Sewer', value: util.sewer });
  if (util.broadband) utilRows.push({ label: 'Broadband', value: util.broadband });

  const factLabel: React.CSSProperties = { fontSize: '9px', color: REPORT.secondary, fontFamily: 'monospace', letterSpacing: '0.04em', textTransform: 'uppercase', flexShrink: 0 };
  const groupHead: React.CSSProperties = { fontSize: '8px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: REPORT.secondary, fontFamily: 'monospace', marginBottom: '6px' };

  return (
    <div className="briefing-section" style={{ background: REPORT.surface, padding: '18px 28px 16px', marginTop: '3px', color: REPORT.body }}>
      <div className="briefing-section-head">
        <SectionTitle>Site &amp; Incentives</SectionTitle>
      </div>

      {sp.incentives && sp.incentives.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
          {sp.incentives.map((inc, i) => {
            const hi = MARQUEE_INCENTIVES.has(inc.label);
            return (
              <div key={i} className="no-break" style={{
                borderRadius: '6px', padding: '8px 12px', minWidth: '140px', flex: '1 1 140px',
                background: hi ? 'rgba(92,107,69,0.10)' : REPORT.white,
                border: `1px solid ${hi ? REPORT.pass + '55' : REPORT.rule}`,
              }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: hi ? REPORT.pass : REPORT.heading, fontFamily: 'monospace', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  {inc.label}
                </div>
                {inc.note && <div style={{ fontSize: '10px', color: REPORT.secondary, marginTop: '2px', lineHeight: 1.45 }}>{inc.note}</div>}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap' }}>
        {facts.length > 0 && (
          <div style={{ flex: '1 1 280px' }}>
            <div style={groupHead}>Site</div>
            {facts.map((f, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'baseline', padding: '3px 0', borderBottom: i < facts.length - 1 ? `1px solid ${REPORT.rule}80` : 'none' }}>
                <span style={factLabel}>{f.label}</span>
                <span style={{ fontSize: '11px', color: REPORT.heading, textAlign: 'right' }}>{f.value}</span>
              </div>
            ))}
          </div>
        )}
        {utilRows.length > 0 && (
          <div style={{ flex: '1 1 280px' }}>
            <div style={groupHead}>Utility service on site</div>
            {utilRows.map((r, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'baseline', padding: '3px 0', borderBottom: i < utilRows.length - 1 ? `1px solid ${REPORT.rule}80` : 'none' }}>
                <span style={factLabel}>{r.label}</span>
                <span style={{ fontSize: '11px', color: r.hi ? REPORT.highlight : REPORT.heading, fontWeight: r.hi ? 600 : 400, textAlign: 'right' }}>{r.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {sp.transport && (
        <div style={{ fontSize: '10.5px', color: REPORT.body, lineHeight: 1.5, marginTop: '10px' }}>
          <span style={{ fontFamily: 'monospace', fontSize: '9px', color: REPORT.secondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Access </span>{sp.transport}
        </div>
      )}

      <SourceFootnote>
        {sp.source || 'KY EconDev listing'}{sp.property_id ? ` · property ${sp.property_id}` : ''}{sp.as_of ? ` · as of ${sp.as_of}` : ''} &middot; verify incentive eligibility and utility service against current program terms before commitment.
      </SourceFootnote>
    </div>
  );
};
